import { useCallback, useEffect, useState } from 'react';
import { newId, type ActionDto, type EquipmentFileDto, type ProcedureItem } from '@app/shared';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { Icon } from '../ui';
import { SubHead } from './admin/SubHead';
import { StepEditor } from './Steps';

/**
 * Библиотека типовых действий: одно действие («Заменить оправку», «Проверить давление») с «как сделать», фото и видео
 * подключается к пунктам у разных машин и номенклатур. Правка здесь — меняется везде.
 */
export function Actions() {
  const { fail, toast } = useApp();
  const [list, setList] = useState<ActionDto[] | null>(null);
  const [files, setFiles] = useState<EquipmentFileDto[]>([]);
  const [edit, setEdit] = useState<ProcedureItem | null>(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const l = await api.actions();
      setList(l);
      setFiles((cur) => [...cur, ...l.flatMap((a) => a.files).filter((f) => !cur.some((c) => c.id === f.id))]);
    } catch (e) {
      fail(e);
    }
  }, [fail]);
  useEffect(() => void load(), [load]);

  async function remove(a: ActionDto) {
    if (!(await platform.confirm(a.uses ? `Удалить «${a.text}» из библиотеки? В ${a.uses} местах пункт останется копией.` : `Удалить «${a.text}»?`))) return;
    try {
      await api.deleteAction(a.id);
      toast('Удалено из библиотеки');
      await load();
    } catch (e) {
      fail(e);
    }
  }

  const shown = (list ?? []).filter((a) => !q.trim() || a.text.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="stack">
      <SubHead title="Типовые действия">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEdit({ id: newId('i'), text: '' })}>
          <Icon name="plus" size={16} />
          Добавить
        </button>
      </SubHead>
      <div className="card flat small">
        Одинаковые действия для разных машин и номенклатур храните здесь. В списках действий и в переналадке — «Из библиотеки»: пункт подключится ссылкой, и правка здесь изменит его везде.
      </div>
      {list && list.length > 5 && (
        <div className="search">
          <Icon name="search" size={18} />
          <input className="input" type="search" placeholder="Поиск" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск действия" />
        </div>
      )}
      {!list && <div className="skeleton" />}
      <div className="list">
        {shown.map((a) => (
          <div key={a.id} className="item">
            <button type="button" className="grow stack plain" style={{ gap: 2 }} onClick={() => setEdit({ id: a.id, text: a.text, details: a.details, fileIds: a.fileIds, actionId: a.id })}>
              <span className="title">{a.text}</span>
              <span className="sub ellipsis">
                {a.details || 'без уточнения'}
                {a.fileIds.length ? ` · файлов: ${a.fileIds.length}` : ''} · подключено: {a.uses}
              </span>
            </button>
            <button type="button" className="icon-btn" aria-label={`Удалить «${a.text}»`} onClick={() => void remove(a)}>
              <Icon name="trash" size={16} />
            </button>
          </div>
        ))}
      </div>
      {edit && (
        <StepEditor
          library
          equipmentId={null}
          item={edit}
          files={files}
          onFile={(f) => setFiles((l) => [...l, f])}
          onClose={() => setEdit(null)}
          onDone={async (i) => {
            // Новое действие: StepEditor сохраняет в библиотеку только по кнопке — здесь создаём сразу.
            if (!edit.actionId && !i.actionId) await api.createAction({ text: i.text, details: i.details ?? null, fileIds: i.fileIds ?? [] });
            setEdit(null);
            toast('Сохранено');
            await load();
          }}
        />
      )}
    </div>
  );
}
